import React, { useEffect } from 'react';
import { Col, Form as AntdForm, Row } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '../../../../../utils/helpers/ApiClient';
import AppBarCMS from '../../../../basic/Navigation/AppBarCMS/AppBarCMS';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import PhdHelpHint from '../../../../common/Phd/PhdHelpHint.jsx';
import { AdminContentBlock, Spinner } from '../../../../common';
import Form from '../../../../antd/form/Form/Form';
import FormButton from '../../../../antd/form/FormButton/FormButton';
import FormButtonsContainer from '../../../../antd/form/FormButtonsContainer/FormButtonsContainer';
import FormDate from '../../../../antd/form/FormDate/FormDate';
import FormFileUpload from '../../../../antd/form/FormFileUpload/FormFileUpload';
import FormSelect from '../../../../antd/form/FormSelect/FormSelect';
import FormTextEditor from '../../../../antd/form/FormTextEditor/FormTextEditor';
import FormTextField from '../../../../antd/form/FormTextField/FormTextField';
import { required } from '../../../../antd/form/FormValidation';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { applyPhdValidationErrors } from '../utils/phdApiFeedback.js';
import { useManualDefendPhdThesisMutation } from '../../../../../redux/services/phd';
import { usePhdReferenceData } from '../usePhdReferenceData.jsx';
import { usePhdDirtyLeaveGuard } from '../usePhdDirtyLeaveGuard.jsx';

const PhdThesesManualDefense = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = AntdForm.useForm();
  const { modal, onSaveFailed, onSaveSuccess, onValuesChange } =
    usePhdDirtyLeaveGuard(form, 'manual-defense');
  const pageTitle = t('Pridať obhájenú historickú prácu');
  const { saveSuccess, saveFailed } = useToastMessages();
  const [manualDefense, { isLoading: isSaving }] =
    useManualDefendPhdThesisMutation();
  const { schoolYearOptions, isLoading } = usePhdReferenceData({
    includeStudents: false,
    includeDepartments: false,
    includeUsers: false,
    includeExaminers: false,
  });

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const handleSubmit = async (values) => {
    const payload = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (key === 'defended_at') {
        payload.append(key, dayjs(value).toISOString());
      } else if (value instanceof File) {
        payload.append(key, value);
      } else {
        payload.append(key, String(value).trim());
      }
    });

    try {
      await manualDefense(payload).unwrap();
      saveSuccess();
      onSaveSuccess(() => navigate(routes.cmsPhdTheses));
    } catch (error) {
      onSaveFailed();
      applyPhdValidationErrors(form, error);
      saveFailed(error);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <>
      {modal}
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsPhdTheses}
        backUrlTooltip={t('Témy dizertačných prác')}
        permissionsBasic="phd-theses"
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako zaevidovať historickú obhajobu?')}
          description={t(
            'Administrátor doktorandského štúdia prepíše údaje z archívnych podkladov. Práca sa hneď zaeviduje ako obhájená a archivovaná; uložené textové údaje zostanú zachované aj bez aktuálneho záznamu v číselníku.',
          )}
        />
        <AdminContentBlock
          icon="fa fa-graduation-cap"
          link={routes.cmsPhdTheses}
          title={pageTitle}
        >
          <div style={{ marginBottom: 16 }}>
            <PhdHelpHint
              label={t('Čo znamená historická obhajoba?')}
              title={t(
                'Túto možnosť použite iba pri už uskutočnenej obhajobe, ktorá nie je evidovaná v bežnom postupe. Voliteľné prepojenia priraďujú aktuálne záznamy; uložené textové údaje zostávajú v archíve nemenné.',
              )}
            />
          </div>
          <Form
            form={form}
            initialValues={{ defended_at: dayjs() }}
            onFinish={handleSubmit}
            onValuesChange={onValuesChange}
          >
            <Row gutter={16}>
              <Col span={12}>
                <FormTextField
                  label={t('Meno študenta/autora')}
                  name="student_display_name"
                  rules={[required(t)]}
                />
              </Col>
              <Col span={12}>
                <FormTextField
                  label={t('Školiteľ')}
                  name="supervisor_display_name"
                  rules={[required(t)]}
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <FormTextField
                  label={t('Názov práce (slovensky)')}
                  name="title_sk"
                  rules={[required(t)]}
                />
              </Col>
              <Col span={12}>
                <FormTextField
                  label={t('Názov práce (anglicky)')}
                  name="title_en"
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <FormSelect
                  label={t('Akademický rok')}
                  name="phd_school_year_id"
                  options={schoolYearOptions}
                  rules={[required(t)]}
                />
              </Col>
              <Col span={12}>
                <FormDate
                  label={t('Dátum obhajoby')}
                  name="defended_at"
                  rules={[required(t)]}
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <FormTextField
                  label={t('Študijný program')}
                  name="study_program_name"
                  rules={[required(t)]}
                />
              </Col>
              <Col span={12}>
                <FormTextField
                  label={t('Študijný odbor')}
                  name="study_field_name"
                  rules={[required(t)]}
                />
              </Col>
            </Row>
            <Row gutter={16}>
              {[1, 2, 3].map((number) => (
                <Col span={8} key={number}>
                  <FormTextField
                    label={t(`Oponent ${number}`)}
                    name={`reviewer${number}`}
                  />
                </Col>
              ))}
            </Row>
            <FormTextEditor
              label={t('Abstrakt (slovensky)')}
              name="abstract_sk"
              imgUploadDirectory="dissertations"
              fileUploadDirectory="dissertations"
            />
            <FormTextEditor
              label={t('Abstrakt (anglicky)')}
              name="abstract_en"
              imgUploadDirectory="dissertations"
              fileUploadDirectory="dissertations"
            />
            <Row gutter={16}>
              <Col span={12}>
                <FormFileUpload label={t('Autoreferát')} name="summary_file" />
              </Col>
              <Col span={12}>
                <FormFileUpload label={t('Text práce')} name="fulltext_file" />
              </Col>
            </Row>
            <FormButtonsContainer>
              <FormButton
                submitOnClick
                submitKey="phd-theses"
                disabled={isSaving}
              >
                {t('Uložiť obhájenú prácu')}
              </FormButton>
            </FormButtonsContainer>
          </Form>
        </AdminContentBlock>
      </div>
    </>
  );
};

export default PhdThesesManualDefense;
